import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

let pushRequest = null;

// Promise-based styled replacement for window.confirm().
// Usage: if (!(await confirmDialog({ title, description, confirmText, destructive }))) return;
// Pass requireText to make the person type it out before Confirm unlocks.
export function confirmDialog(opts = {}) {
  return new Promise((resolve) => {
    if (!pushRequest) {
      resolve(window.confirm([opts.title, opts.description].filter(Boolean).join('\n\n') || 'Are you sure?'));
      return;
    }
    pushRequest({ ...opts, resolve });
  });
}

export function ConfirmDialogHost() {
  const [req, setReq] = useState(null);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    pushRequest = (r) => setReq((prev) => {
      if (prev) prev.resolve(false);
      setTyped('');
      return r;
    });
    return () => { pushRequest = null; };
  }, []);

  const close = (result) => {
    if (req) req.resolve(result);
    setReq(null);
    setTyped('');
  };

  const needsText = !!req?.requireText;
  const textOk = !needsText || typed.trim().toLowerCase() === String(req.requireText).trim().toLowerCase();

  return (
    <AlertDialog open={!!req} onOpenChange={(open) => { if (!open) close(false); }}>
      <AlertDialogContent className="bg-surface-1 border border-white/10 rounded-xl max-w-[480px]" data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white text-base font-semibold" data-testid="confirm-dialog-title">
            {req?.title || 'Are you sure?'}
          </AlertDialogTitle>
          {req?.description ? (
            <AlertDialogDescription className="text-slate-400 text-sm whitespace-pre-line leading-relaxed" data-testid="confirm-dialog-description">
              {req.description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        {needsText ? (
          <div className="space-y-1">
            <p className="text-slate-400 text-xs">
              Type <span className="text-white font-semibold">{req.requireText}</span> to confirm.
            </p>
            <Input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={String(req.requireText)}
              className="bg-white/[0.03] border-white/10 text-white h-9"
              data-testid="confirm-dialog-type-input" />
          </div>
        ) : null}
        <AlertDialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => close(false)}
            className="text-slate-300 hover:text-white hover:bg-white/[0.06]"
            data-testid="confirm-dialog-cancel">
            {req?.cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={() => close(true)}
            disabled={!textOk}
            className={req?.destructive
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-violet-600 hover:bg-violet-700 text-white'}
            data-testid="confirm-dialog-confirm">
            {req?.confirmText || 'Confirm'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
